import { styled } from '@mui/material/styles';
import { Radio, RadioGroup, FormControlLabel, FormControl } from '@mui/material';
import { RadioSize, RadioValidationState } from './Radio.types';
import { designTokens } from '../../tokens';

/**
 * Size-specific styles for radios
 */
const getSizeStyles = (size: RadioSize) => {
  switch (size) {
    case 'sm':
      return {
        '& .MuiRadio-root': {
          padding: designTokens.spacing[1],
          '& svg': {
            fontSize: '1rem',
          },
        },
        '& .MuiFormControlLabel-label': {
          fontSize: designTokens.typography.fontSize.xs,
        },
      };
    case 'md':
      return {
        '& .MuiRadio-root': {
          padding: designTokens.spacing[2],
          '& svg': {
            fontSize: '1.25rem',
          },
        },
        '& .MuiFormControlLabel-label': {
          fontSize: designTokens.typography.fontSize.sm,
        },
      };
    case 'lg':
      return {
        '& .MuiRadio-root': {
          padding: designTokens.spacing[2],
          '& svg': {
            fontSize: '1.5rem',
          },
        },
        '& .MuiFormControlLabel-label': {
          fontSize: designTokens.typography.fontSize.base,
        },
      };
    default:
      return {};
  }
};

/**
 * Validation state styles
 */
const getValidationStyles = (validationState: RadioValidationState) => {
  const tokens = designTokens.colors;

  switch (validationState) {
    case 'error':
      return {
        '& .MuiRadio-root': {
          color: tokens.error[500],
          '&.Mui-checked': {
            color: tokens.error[700],
          },
        },
        '& .MuiFormHelperText-root': {
          color: tokens.error[700],
        },
        '& .MuiFormLabel-root': {
          color: tokens.error[700],
        },
      };

    case 'success':
      return {
        '& .MuiRadio-root': {
          color: tokens.success[500],
          '&.Mui-checked': {
            color: tokens.success[700],
          },
        },
        '& .MuiFormHelperText-root': {
          color: tokens.success[700],
        },
        '& .MuiFormLabel-root': {
          color: tokens.success[700],
        },
      };

    case 'warning':
      return {
        '& .MuiRadio-root': {
          color: tokens.warning[500],
          '&.Mui-checked': {
            color: tokens.warning[700],
          },
        },
        '& .MuiFormHelperText-root': {
          color: tokens.warning[700],
        },
        '& .MuiFormLabel-root': {
          color: tokens.warning[700],
        },
      };

    case 'default':
    default:
      return {
        '& .MuiRadio-root': {
          color: tokens.neutral[600],
          '&.Mui-checked': {
            color: tokens.primary[600],
          },
          '&:hover': {
            backgroundColor: `${tokens.primary[50]}40`,
          },
        },
      };
  }
};

/**
 * Styled Radio component
 */
export const StyledRadio = styled(Radio)(() => ({
  transition: `all ${designTokens.transitions.duration.fast} ${designTokens.transitions.timing.easeInOut}`,

  '&.Mui-disabled': {
    color: designTokens.colors.neutral[400],
  },
}));

/**
 * Styled FormControlLabel wrapper for individual radio
 */
export const StyledFormControlLabel = styled(FormControlLabel, {
  shouldForwardProp: (prop) =>
    !['radioSize', 'validationState'].includes(prop as string),
})<{
  radioSize: RadioSize;
  validationState: RadioValidationState;
}>(({ radioSize, validationState }) => ({
  ...getSizeStyles(radioSize),
  ...getValidationStyles(validationState),

  margin: 0,
  fontFamily: designTokens.typography.fontFamily.primary,

  '& .MuiFormControlLabel-label': {
    fontWeight: designTokens.typography.fontWeight.regular,
    lineHeight: designTokens.typography.lineHeight.normal,
    userSelect: 'none',
  },

  '&.Mui-disabled .MuiFormControlLabel-label': {
    color: designTokens.colors.neutral[400],
  },
}));

/**
 * Styled RadioGroup component
 */
export const StyledRadioGroup = styled(RadioGroup)(() => ({
  gap: designTokens.spacing[1],
}));

/**
 * Styled FormControl wrapper for radio group
 */
export const StyledFormControl = styled(FormControl, {
  shouldForwardProp: (prop) =>
    !['radioSize', 'validationState'].includes(prop as string),
})<{
  radioSize: RadioSize;
  validationState: RadioValidationState;
}>(({ radioSize, validationState }) => ({
  ...getSizeStyles(radioSize),
  ...getValidationStyles(validationState),

  // FormLabel styles
  '& .MuiFormLabel-root': {
    fontSize: designTokens.typography.fontSize.sm,
    fontWeight: designTokens.typography.fontWeight.medium,
    marginBottom: designTokens.spacing[1],
    color: designTokens.colors.neutral[700],

    '&.Mui-focused': {
      color: designTokens.colors.primary[600],
    },
  },

  // Helper text styles
  '& .MuiFormHelperText-root': {
    fontSize: designTokens.typography.fontSize.xs,
    marginTop: designTokens.spacing[1],
    marginLeft: 0,
  },
}));
